import React from "react";
import { Film, Loader2, Download, Smartphone } from "lucide-react";
import { toast } from "sonner";
import api, { API } from "../../lib/api";

// Reusable button that generates (server-side) and downloads an MP4 reel for a
// given section. `format` can be "landscape" (16:9) or "vertical" (9:16 Story).
export const ReelDownloadButton = ({
  section,
  format = "landscape",
  label = "Download Reel",
  className = "",
}) => {
  const [state, setState] = React.useState("idle"); // idle | working | done
  const pollRef = React.useRef(null);
  const isVertical = format === "vertical";
  const q = isVertical ? "?format=vertical" : "";

  React.useEffect(() => () => clearInterval(pollRef.current), []);

  const triggerDownload = () => {
    const a = document.createElement("a");
    a.href = `${API}/reel/${section}/download${q}`;
    a.download = `asanight-${section}-${isVertical ? "story" : "reel"}.mp4`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const start = async () => {
    if (state === "working") return;
    setState("working");
    try {
      // If already generated, download straight away
      const { data: st } = await api.get(`/reel/${section}/status${q}`);
      if (st.ready) {
        triggerDownload();
        setState("done");
        toast.success("Your reel is downloading. Post it and tag us!");
        setTimeout(() => setState("idle"), 4000);
        return;
      }
      await api.post(`/reel/${section}/generate${q}`);
      toast.info("Creating your reel… this can take a few minutes for large sets.");
      pollRef.current = setInterval(async () => {
        try {
          const { data } = await api.get(`/reel/${section}/status${q}`);
          if (data.ready) {
            clearInterval(pollRef.current);
            triggerDownload();
            setState("done");
            toast.success("Your reel is downloading. Post it and tag us!");
            setTimeout(() => setState("idle"), 4000);
          } else if (data.status === "error") {
            clearInterval(pollRef.current);
            setState("idle");
            toast.error("Couldn't create the reel. Please try again.");
          }
        } catch {
          /* keep polling */
        }
      }, 3000);
    } catch {
      setState("idle");
      toast.error("Couldn't start the reel. Please try again.");
    }
  };

  const Icon = isVertical ? Smartphone : Film;

  return (
    <button
      onClick={start}
      disabled={state === "working"}
      data-testid={`reel-download-${section}${isVertical ? "-vertical" : ""}`}
      className={
        className ||
        "inline-flex items-center gap-2 bg-gold text-night text-sm font-semibold px-5 py-2.5 rounded-full hover:bg-gold-light transition-colors disabled:opacity-60"
      }
    >
      {state === "working" ? (
        <>
          <Loader2 className="w-4 h-4 animate-spin" /> Creating reel…
        </>
      ) : state === "done" ? (
        <>
          <Download className="w-4 h-4" /> Downloading…
        </>
      ) : (
        <>
          <Icon className="w-4 h-4" /> {label}
        </>
      )}
    </button>
  );
};

export default ReelDownloadButton;
